import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, Save, Eye, Upload, Trash2, GripVertical, Image, FileText, Gift, Users, MapPin, Pencil, Palette, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import ImageCropEditor from "../components/ImageCropEditor";
import ThemeCustomizer from "../components/ThemeCustomizer";
import PixConfig from "../components/PixConfig";


const BLOCK_TYPES = {
  cover: { icon: Image, label: "Capa", color: "#B3121B" },
  story: { icon: FileText, label: "Nossa História", color: "#6E0F1A" },
  gallery: { icon: Image, label: "Galeria", color: "#CFAFE6" },
  gifts: { icon: Gift, label: "Presentes", color: "#567D50" },
  rsvp: { icon: Users, label: "RSVP", color: "#F3C9CF" },
  location: { icon: MapPin, label: "Localização", color: "#B3121B" }
};

export default function SiteEditor() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    partner1_name: "",
    partner2_name: "",
    wedding_date: "",
    event_location: "",
    story: ""
  });
  const [blocks, setBlocks] = useState([]);
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [draggedGalleryIndex, setDraggedGalleryIndex] = useState(null);
  
  // Image crop editor states
  const [cropEditorOpen, setCropEditorOpen] = useState(false);
  const [cropImageFile, setCropImageFile] = useState(null);
  const [cropImageType, setCropImageType] = useState("cover");
  const [editingGalleryIndex, setEditingGalleryIndex] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("content"); // "content" or "theme"

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const res = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setSite(data);
            setFormData({
              partner1_name: data.partner1_name || "",
              partner2_name: data.partner2_name || "",
              wedding_date: data.wedding_date || "",
              event_location: data.event_location || "",
              story: data.story || "",
              gifts_message: data.gifts_message || ""
            });
            setBlocks(data.blocks || []);
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSite();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Include gallery_images in the save to preserve the order
      const saveData = {
        ...formData,
        blocks,
        gallery_images: site?.gallery_images || []
      };
      
      const res = await fetch(`${API}/sites/${site.site_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(saveData)
      });

      if (res.ok) {
        toast.success("Site salvo com sucesso!");
        const updated = await res.json();
        setSite(updated);
      } else {
        throw new Error('Erro ao salvar');
      }
    } catch (error) {
      toast.error("Erro ao salvar site");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Open crop editor instead of uploading directly
    setCropImageFile(file);
    setCropImageType(type);
    setCropEditorOpen(true);
    
    // Clear the input
    e.target.value = "";
  };

  const handleCroppedImageSave = async (croppedFile) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', croppedFile);

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/upload-image?image_type=${cropImageType}`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        toast.success("Imagem enviada!");
        
        // Refresh site data
        const siteRes = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (siteRes.ok) {
          const siteData = await siteRes.json();
          setSite(siteData);
        }
      }
    } catch (error) {
      toast.error("Erro ao enviar imagem");
    }
  };

  const handleDeleteGalleryImage = async (index) => {
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/gallery-image/${index}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success("Imagem removida!");
        // Refresh site data
        const siteRes = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (siteRes.ok) {
          const siteData = await siteRes.json();
          setSite(siteData);
        }
      }
    } catch (error) {
      toast.error("Erro ao remover imagem");
    }
  };

  const toggleBlockVisibility = (blockId) => {
    setBlocks(prev => prev.map(b => 
      b.id === blockId ? { ...b, visible: !b.visible } : b
    ));
  };

  const handleDragStart = (e, index) => {
    setDraggedBlock(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedBlock === null || draggedBlock === index) return;
    
    const newBlocks = [...blocks];
    const draggedItem = newBlocks[draggedBlock];
    newBlocks.splice(draggedBlock, 1);
    newBlocks.splice(index, 0, draggedItem);
    
    // Update order
    newBlocks.forEach((b, i) => b.order = i);
    
    setBlocks(newBlocks);
    setDraggedBlock(index);
  };

  const handleDragEnd = () => {
    setDraggedBlock(null);
  };

  // Gallery drag and drop functions
  const handleGalleryDragStart = (e, index) => {
    setDraggedGalleryIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleGalleryDragOver = (e, index) => {
    e.preventDefault();
    if (draggedGalleryIndex === null || draggedGalleryIndex === index) return;
    
    const newImages = [...(site?.gallery_images || [])];
    const draggedImage = newImages[draggedGalleryIndex];
    newImages.splice(draggedGalleryIndex, 1);
    newImages.splice(index, 0, draggedImage);
    
    setSite(prev => ({ ...prev, gallery_images: newImages }));
    setDraggedGalleryIndex(index);
  };

  const handleGalleryDragEnd = async () => {
    setDraggedGalleryIndex(null);
    // A ordem será salva quando o usuário clicar em "Salvar"
    // Mostramos uma dica para lembrar
    toast.info("Clique em 'Salvar' para confirmar a nova ordem", { duration: 2000 });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-[#6E0F1A]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-[#B3121B] fill-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Editor do Site</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {site?.is_published && (
              <Button
                variant="outline"
                onClick={() => window.open(`/casamento/${site.slug}`, '_blank')}
                className="border-[#B3121B] text-[#B3121B] rounded-full"
              >
                <Eye className="w-4 h-4 mr-2" />
                Visualizar
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full"
              data-testid="save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-[#F3C9CF] overflow-x-auto">
          <button
            onClick={() => setActiveTab("content")}
            className={`px-6 py-3 font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "content"
                ? "border-[#B3121B] text-[#B3121B]"
                : "border-transparent text-gray-500 hover:text-[#6E0F1A]"
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Conteúdo
          </button>
          <button
            onClick={() => setActiveTab("theme")}
            className={`px-6 py-3 font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "theme"
                ? "border-[#B3121B] text-[#B3121B]"
                : "border-transparent text-gray-500 hover:text-[#6E0F1A]"
            }`}
          >
            <Palette className="w-4 h-4 inline mr-2" />
            Tema
          </button>
          <button
            onClick={() => setActiveTab("payments")}
            className={`px-6 py-3 font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "payments"
                ? "border-[#B3121B] text-[#B3121B]"
                : "border-transparent text-gray-500 hover:text-[#6E0F1A]"
            }`}
          >
            <CreditCard className="w-4 h-4 inline mr-2" />
            Pagamentos
          </button>
        </div>

        {activeTab === "theme" ? (
          <ThemeCustomizer 
            site={site} 
            onUpdate={(updated) => setSite(updated)} 
          />
        ) : activeTab === "payments" ? (
          <div className="max-w-2xl">
            <PixConfig 
              site={site} 
              onUpdate={(updated) => setSite(updated)} 
            />
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="font-serif text-xl text-[#6E0F1A]">Informações Básicas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="partner1_name" className="text-[#6E0F1A]">Nome do primeiro noivo(a)</Label>
                    <Input
                      id="partner1_name"
                      name="partner1_name"
                      value={formData.partner1_name}
                      onChange={handleChange}
                      className="input-wedding mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="partner2_name" className="text-[#6E0F1A]">Nome do segundo noivo(a)</Label>
                    <Input
                      id="partner2_name"
                      name="partner2_name"
                      value={formData.partner2_name}
                      onChange={handleChange}
                      className="input-wedding mt-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="wedding_date" className="text-[#6E0F1A]">Data do Casamento</Label>
                    <Input
                      id="wedding_date"
                      name="wedding_date"
                      type="date"
                      value={formData.wedding_date}
                      onChange={handleChange}
                      className="input-wedding mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event_location" className="text-[#6E0F1A]">Local do Evento</Label>
                    <Input
                      id="event_location"
                      name="event_location"
                      value={formData.event_location}
                      onChange={handleChange}
                      className="input-wedding mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="story" className="text-[#6E0F1A]">Nossa História</Label>
                  <Textarea
                    id="story"
                    name="story"
                    value={formData.story}
                    onChange={handleChange}
                    className="input-wedding mt-2 min-h-[150px]"
                    placeholder="Conte como vocês se conheceram..."
                  />
                </div>
                <div>
                  <Label htmlFor="gifts_message" className="text-[#6E0F1A]">Mensagem da Seção de Presentes</Label>
                  <Textarea
                    id="gifts_message"
                    name="gifts_message"
                    value={formData.gifts_message || ""}
                    onChange={handleChange}
                    className="input-wedding mt-2 min-h-[80px]"
                    placeholder="Sua presença é o nosso maior presente, mas se desejar nos presentear, escolha um item da nossa lista ou faça uma contribuição livre."
                  />
                  <p className="text-xs text-[#6E0F1A]/50 mt-1">
                    Texto exibido na seção de presentes. Deixe em branco para usar o texto padrão.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cover Image */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="font-serif text-xl text-[#6E0F1A]">Imagem de Capa</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#6E0F1A]/60 mb-4">
                  Esta imagem será exibida no topo do seu site. Recomendamos uma imagem horizontal (16:9).
                </p>
                <div 
                  className="border-2 border-dashed border-[#F3C9CF] rounded-xl p-4 text-center cursor-pointer hover:border-[#B3121B] transition-colors"
                  onClick={() => document.getElementById('cover-input').click()}
                >
                  {site?.cover_image ? (
                    <div className="relative group">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${site.cover_image}`} 
                        alt="Cover" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <div className="bg-white/90 rounded-full px-4 py-2 flex items-center gap-2">
                          <Pencil className="w-4 h-4 text-[#6E0F1A]" />
                          <span className="text-[#6E0F1A] font-medium">Alterar imagem</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-12">
                      <Upload className="w-12 h-12 text-[#B3121B] mx-auto mb-4" />
                      <p className="text-[#6E0F1A] font-medium mb-1">Clique para fazer upload da capa</p>
                      <p className="text-sm text-[#6E0F1A]/60">JPG, PNG ou WebP. Recomendado: 1920x1080px</p>
                    </div>
                  )}
                </div>
                <input
                  id="cover-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'cover')}
                  className="hidden"
                />
              </CardContent>
            </Card>

            {/* Gallery */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="font-serif text-xl text-[#6E0F1A]">Galeria de Fotos</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#6E0F1A]/60 mb-4">
                  Adicione fotos do casal. <strong>Arraste para reordenar</strong> as imagens na grade.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  {site?.gallery_images?.map((img, index) => (
                    <div 
                      key={`${img}-${index}`} 
                      className={`relative group aspect-square cursor-move ${
                        draggedGalleryIndex === index ? 'opacity-50 scale-95' : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleGalleryDragStart(e, index)}
                      onDragOver={(e) => handleGalleryDragOver(e, index)}
                      onDragEnd={handleGalleryDragEnd}
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                        alt={`Gallery ${index}`}
                        className="w-full h-full object-cover rounded-xl pointer-events-none"
                      />
                      {/* Drag handle indicator */}
                      <div className="absolute top-2 left-2 bg-white/80 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="w-4 h-4 text-[#6E0F1A]" />
                      </div>
                      {/* Position number */}
                      <div className="absolute top-2 right-2 bg-[#B3121B] text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                        {index + 1}
                      </div>
                      {/* Delete button */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-white/90 hover:bg-white text-[#6E0F1A] rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGalleryImage(index);
                          }}
                          title="Remover imagem"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div 
                    className="border-2 border-dashed border-[#F3C9CF] rounded-xl aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-[#B3121B] hover:bg-[#F3C9CF]/10 transition-all"
                    onClick={() => document.getElementById('gallery-input').click()}
                  >
                    <Upload className="w-8 h-8 text-[#B3121B] mb-2" />
                    <span className="text-sm text-[#6E0F1A]/60">Adicionar</span>
                  </div>
                </div>
                <input
                  id="gallery-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'gallery')}
                  className="hidden"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Blocks */}
          <div className="space-y-6">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="font-serif text-xl text-[#6E0F1A]">Seções do Site</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#6E0F1A]/60 mb-4">
                  Arraste para reordenar. Ative ou desative seções.
                </p>
                <div className="space-y-2">
                  {blocks.sort((a, b) => a.order - b.order).map((block, index) => {
                    const BlockIcon = BLOCK_TYPES[block.type]?.icon || FileText;
                    return (
                      <div
                        key={block.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 p-3 bg-[#FAFAFA] rounded-lg cursor-move transition-all ${
                          draggedBlock === index ? 'opacity-50 scale-95' : ''
                        }`}
                        data-testid={`block-${block.type}`}
                      >
                        <GripVertical className="w-4 h-4 text-[#6E0F1A]/40" />
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${BLOCK_TYPES[block.type]?.color}20` }}
                        >
                          <BlockIcon 
                            className="w-4 h-4" 
                            style={{ color: BLOCK_TYPES[block.type]?.color }}
                          />
                        </div>
                        <span className="flex-1 text-sm text-[#6E0F1A]">
                          {BLOCK_TYPES[block.type]?.label || block.type}
                        </span>
                        <Switch
                          checked={block.visible}
                          onCheckedChange={() => toggleBlockVisibility(block.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* URL Info */}
            <Card className="card-dashboard">
              <CardContent className="pt-6">
                <p className="text-sm text-[#6E0F1A]/60 mb-2">URL do seu site:</p>
                <code className="text-sm bg-[#F3C9CF]/20 px-3 py-2 rounded-lg block text-[#6E0F1A] break-all">
                  /casamento/{site?.slug}
                </code>
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </main>

      {/* Image Crop Editor */}
      <ImageCropEditor
        open={cropEditorOpen}
        onClose={() => {
          setCropEditorOpen(false);
          setCropImageFile(null);
        }}
        imageFile={cropImageFile}
        onSave={handleCroppedImageSave}
        onSaveOriginal={handleCroppedImageSave}
        aspectRatio={cropImageType === "cover" ? 16 / 9 : 1}
        imageType={cropImageType}
        title={cropImageType === "cover" ? "Ajustar Imagem de Capa" : "Ajustar Foto da Galeria"}
      />
    </div>
  );
}
